import { AuiUploader, SAlert, SBox, SLoader } from "@avalara/skylab-sdk/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { testIDs } from "../../../end-to-end-tests/constants/testIDs";
import {
    fetchCompanies,
    selectCompanies,
    selectIsLoadingCompanies,
} from "../../app/companyGroupsSlice";
import { loadDataTransfers } from "../../app/dataTransferSlice";
import { selectSession } from "../../app/sessionSlice";
import type { AppDispatch } from "../../app/types";
import getConfig from "../../config";
import toast from "../../hooks/toast";
import { useTokenProvider } from "../../hooks/useTokenProvider";
import { redirectToLoginPage } from "../../shared/sessionUtility";
import { postDataTransferImport, postDataTransferImportPrepare } from "./dataTransferApi";
import { buildDataTransferImportKey } from "./importKey";
import ImportCompanyMappingDialog, {
    type CompanyMapping,
    type TargetCompany,
} from "./ImportCompanyMappingDialog";
import ImportOverwriteConfirmDialog from "./ImportOverwriteConfirmDialog";
import type { DataTransferImportManifest, FvsFileDetail } from "./types";

const [showToast] = toast();

// Config is retrieved lazily inside functions to support runtime configuration. Named to avoid
// colliding with the env-keyed getFvsConfigurationId(env) exported from src/config.ts.
const resolveFvsConfigurationId = () => getConfig().fvsConfigurationId;

const UPLOADER_ID = "aui-uploader-data-transfer";
const PACKAGE_EXTENSION = "avlr";

type Stage = "upload" | "scanning" | "ready" | "mapping" | "confirm";

interface AttachEvent {
    detail: { files: FvsFileDetail[] };
}

interface ValidateEvent {
    detail: {
        filesPassedValidation: FvsFileDetail[];
        filesFailedValidation: Array<{ message: string }>;
    };
}

const getExtension = (name: string): string => name.split(".").pop()?.toLowerCase() ?? "";

type NormalizedEnvironment = "production" | "sandbox" | "other";

/**
 * Normalise an environment to a single vocabulary. The package's `exportedEnvironment` is core's
 * ENVIRONMENT_TAG ("Production", "Sandbox", "UAT / Beta", ...) while the session environment is the
 * IAM code ("prd", "sbx", "qa", ...), so both have to be mapped before they can be compared.
 */
const normalizeEnvironment = (value: string | null | undefined): NormalizedEnvironment | null => {
    if (!value) return null;
    const normalized = value.trim().toLowerCase();
    if (normalized === "production" || normalized === "prd") return "production";
    if (normalized === "sandbox" || normalized === "sbx") return "sandbox";
    return "other";
};

/**
 * Cross-environment import rule, mirroring core's canonical check
 * (DataMigrationProcessingController.php:3628): an import is compatible when the package's
 * environment matches the target, when it's a Production<->Sandbox pair, or when the package came
 * from any non-production/non-sandbox environment. Anything we don't recognise on either side is
 * treated as compatible so the warning never fires on a guess.
 */
const isCrossEnvironmentImport = (
    exportedEnvironment: string | null | undefined,
    sessionEnvironment: string | null | undefined
): boolean => {
    const source = normalizeEnvironment(exportedEnvironment);
    const target = normalizeEnvironment(sessionEnvironment);
    if (!source || !target || source === "other" || source === target) return false;
    const prodSandboxPair =
        (source === "production" && target === "sandbox") ||
        (source === "sandbox" && target === "production");
    return !prodSandboxPair;
};

/**
 * Import half of Data Transfer, modelled on CC's data migration import. The file goes to S3
 * through the FVS uploader; the service then reads the package by `key` + `validationId`.
 * Flow: upload -> prepare (service reads the package manifest) -> map this account's companies to
 * incoming companies -> type the account name to confirm -> POST import.
 */
const DataTransferImportPanel: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const tokenProvider = useTokenProvider();
    const session = useSelector(selectSession) as {
        accountId?: string | number | null;
        activeCompany?: { name?: string | null };
        environment?: string | null;
    };
    const selectedCompanies = useSelector(selectCompanies) as TargetCompany[] | undefined;
    const companies = useMemo(() => selectedCompanies ?? [], [selectedCompanies]);
    const isLoadingCompanies = useSelector(selectIsLoadingCompanies) as boolean;
    // eslint-disable-next-line no-undef -- global element type declared by the Skylab SDK
    const uploaderRef = useRef<HTMLAuiUploaderElement>(null);
    const pendingFile = useRef<FvsFileDetail | null>(null);
    // Supersedes an in-flight prepare: bumped on every validate and on reset, so a late response
    // from a run the user has moved on from (or cancelled) is ignored instead of forcing "ready".
    const validateSeq = useRef(0);

    const accountId = session?.accountId;
    const hasAccountId = accountId !== null && accountId !== undefined && accountId !== "";

    const [stage, setStage] = useState<Stage>("upload");
    const [file, setFile] = useState<FvsFileDetail | null>(null);
    const [manifest, setManifest] = useState<DataTransferImportManifest | null>(null);
    const [mapping, setMapping] = useState<CompanyMapping>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const incoming = useMemo(() => manifest?.companies ?? [], [manifest]);

    // The confirm gate asks the user to retype this; it's the active company's name, not an account
    // name (the session carries no account name), so the copy refers to it as the company.
    const companyName = session?.activeCompany?.name ?? "";

    // Warn only when the package's environment is genuinely incompatible with the one we're
    // importing into (see isCrossEnvironmentImport) — the mistake this deliberate, irreversible flow
    // exists to catch. Both values are in hand once the manifest loads.
    const packageEnvironment = manifest?.exportedEnvironment ?? null;
    const sessionEnvironment = session?.environment ?? null;
    const environmentMismatch = isCrossEnvironmentImport(packageEnvironment, sessionEnvironment);

    // The mappings that will actually be POSTed: only destination companies still present in the
    // current list with an incoming company chosen. Deriving the confirm-dialog count from the same
    // list keeps the stated impact and the request in sync even if the company list is refreshed.
    const mappings = useMemo(
        () =>
            companies
                .filter(target => mapping[target.value])
                .map(target => {
                    const source = incoming.find(
                        company => String(company.id) === mapping[target.value]
                    );
                    return {
                        sourceCompanyId: Number(mapping[target.value]),
                        sourceCompanyName: source?.name ?? "",
                        targetCompanyId: Number(target.value),
                        targetCompanyName: target.label,
                    };
                }),
        [companies, mapping, incoming]
    );

    useEffect(() => {
        dispatch(fetchCompanies());
    }, [dispatch]);

    const resetUploader = useCallback(() => {
        (uploaderRef.current as (HTMLElement & { reset?: () => void }) | null)?.reset?.();
    }, []);

    const reset = useCallback(() => {
        resetUploader();
        // Invalidate any prepare request still in flight so its late response can't revive the flow.
        validateSeq.current += 1;
        pendingFile.current = null;
        setFile(null);
        setManifest(null);
        setMapping({});
        setIsSubmitting(false);
        setStage("upload");
    }, [resetUploader]);

    const handleApiError = (error: unknown, fallback: string): void => {
        const response = (error as { response?: { status?: number; data?: { title?: string } } })
            ?.response;
        if (response?.status === 401) {
            redirectToLoginPage();
        } else {
            showToast("error", response?.data?.title || fallback);
        }
    };

    const handleAttach = (event: AttachEvent): void => {
        const attached = event?.detail?.files?.[0];
        if (!attached) return;
        // Extension-only check (unlike ImportCertificates, which also checks file.type): `.avlr` has
        // no registered MIME type, so `file.type` is "" or browser-dependent and useless here. FVS
        // validates the package server-side regardless.
        if (getExtension(attached.name) !== PACKAGE_EXTENSION) {
            showToast("error", "File type not allowed. Upload an .avlr transfer package.");
            resetUploader();
            return;
        }
        pendingFile.current = attached;
        setFile(attached);
        setStage("scanning");
    };

    const handleValidate = async (event: ValidateEvent): Promise<void> => {
        const { filesPassedValidation = [], filesFailedValidation = [] } = event.detail;
        if (filesFailedValidation.length > 0) {
            showToast("error", `${filesFailedValidation[0].message}`);
            reset();
            return;
        }
        const passed = filesPassedValidation[0] ?? pendingFile.current;
        if (!passed) return;
        const seq = (validateSeq.current += 1);
        try {
            // The service opens the package and reads its manifest (as CC's upload step does).
            const result = await postDataTransferImportPrepare({
                key: passed.key,
                fileName: passed.name,
                validationId: passed.validationId,
            });
            // The user cancelled or re-uploaded while this was in flight — drop the stale response.
            if (seq !== validateSeq.current) return;
            if (!result.companies?.length) {
                showToast("error", "No companies were found in this package.");
                reset();
                return;
            }
            setFile(passed);
            setManifest(result);
            setMapping({});
            setStage("ready");
        } catch (error) {
            if (seq !== validateSeq.current) return;
            handleApiError(error, "We couldn't read this package.");
            reset();
        }
    };

    const handleImport = async (): Promise<void> => {
        // Guard against a double submit (e.g. a fast double-click before the button's disabled
        // state paints) so a single confirmation can only ever POST one import. Also refuse an empty
        // mappings array: a company-list refresh can drop the last mapped company while the confirm
        // dialog is open, which the server would reject with a generic failure toast.
        if (!file || isSubmitting || mappings.length === 0) return;
        setIsSubmitting(true);
        try {
            await postDataTransferImport({
                key: file.key,
                fileName: file.name,
                validationId: file.validationId,
                mappings,
            });
            reset();
            showToast("success", "Import started. Track its progress in Transfer history.");
            dispatch(loadDataTransfers(1));
        } catch (error) {
            handleApiError(error, "Failed to start the import.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const refreshTargets = (): void => {
        dispatch(fetchCompanies());
    };

    const mappedTotal = mappings.length;

    return (
        <SBox className="margin-bottom-xl" data-testid={testIDs.dataTransfer.importPanel}>
            <h2 className="margin-top-none">Import data</h2>
            <p className="margin-top-none">
                Upload a transfer package exported from another CertCapture account.
            </p>
            <SAlert status="warning" noDismiss className="margin-bottom-md">
                Importing overwrites existing data for the mapped companies, and changes may flow to
                AvaTax and other connected Avalara products. Certificates imported into this account
                also count toward its usage.
            </SAlert>

            <div hidden={stage !== "upload"}>
                {hasAccountId ? (
                    <AuiUploader
                        id={UPLOADER_ID}
                        ref={uploaderRef}
                        callbackUrl="/"
                        data-testid={testIDs.dataTransfer.importUploader}
                        multiple={false}
                        accept={`.${PACKAGE_EXTENSION}`}
                        stagingInlineCta={false}
                        stagingExternalCta={false}
                        configurationId={resolveFvsConfigurationId()}
                        modifyDestinationFileKey={(fileName: string) =>
                            buildDataTransferImportKey(accountId, fileName)
                        }
                        requestCurrentAccessToken={tokenProvider}
                        onS-attach={e => handleAttach(e as unknown as AttachEvent)}
                        onS-validate={e => handleValidate(e as unknown as ValidateEvent)}
                        onS-error={reset}>
                        <div slot="guidelines">
                            Only .avlr transfer packages exported from CertCapture are accepted.
                        </div>
                    </AuiUploader>
                ) : (
                    <SAlert status="error" noDismiss>
                        We couldn&apos;t determine your account. Reload the page and try again.
                    </SAlert>
                )}
            </div>

            {stage === "scanning" ? (
                <div
                    role="status"
                    aria-live="polite"
                    className="data-transfer-status"
                    data-testid={testIDs.dataTransfer.importScanning}>
                    <SLoader loading />
                    <h3 className="margin-all-none">Scanning {file?.name}</h3>
                    <p className="margin-all-none">
                        Checking the file and reading the companies in the package.
                    </p>
                    <button
                        type="button"
                        className="secondary"
                        data-testid={testIDs.dataTransfer.importCancelScan}
                        onClick={reset}>
                        Cancel
                    </button>
                </div>
            ) : null}

            {file && stage !== "upload" && stage !== "scanning" ? (
                <React.Fragment>
                    {environmentMismatch ? (
                        <SAlert
                            status="warning"
                            noDismiss
                            className="margin-bottom-md"
                            data-testid={testIDs.dataTransfer.importEnvWarning}>
                            This package was exported from <strong>{packageEnvironment}</strong>,
                            but you&apos;re importing into <strong>{sessionEnvironment}</strong>.
                            Make sure you mean to move data between environments before continuing.
                        </SAlert>
                    ) : null}
                    <div
                        className="data-transfer-file"
                        data-testid={testIDs.dataTransfer.importFileChip}>
                        <div>
                            <strong>{file.name}</strong>
                            <div className="data-transfer-count">
                                {incoming.length} {incoming.length === 1 ? "company" : "companies"}{" "}
                                in package
                                {manifest?.sourceAccountName
                                    ? ` · exported from ${manifest.sourceAccountName}`
                                    : ""}
                            </div>
                        </div>
                        <button
                            type="button"
                            className="secondary"
                            disabled={isSubmitting}
                            data-testid={testIDs.dataTransfer.importRemoveFile}
                            onClick={reset}>
                            Remove
                        </button>
                        <button
                            type="button"
                            className="primary"
                            disabled={isSubmitting}
                            data-testid={testIDs.dataTransfer.importReviewMapping}
                            onClick={() => setStage("mapping")}>
                            Review company mapping
                        </button>
                    </div>
                </React.Fragment>
            ) : null}

            <ImportCompanyMappingDialog
                open={stage === "mapping"}
                sourceAccountName={manifest?.sourceAccountName}
                incoming={incoming}
                targets={companies}
                isLoadingTargets={isLoadingCompanies}
                mapping={mapping}
                // Count only destination companies still in the current list (what the POST sends),
                // so a refresh that drops a mapped company can't leave Continue enabled with an
                // empty import.
                mappedTotal={mappedTotal}
                onChange={(targetValue, incomingId) =>
                    setMapping(current => ({ ...current, [targetValue]: incomingId }))
                }
                onRefreshTargets={refreshTargets}
                // SDialog emits s-dismiss whenever it closes, including when we move to the next
                // step, so only step back if this dialog is still the active one.
                onCancel={() => setStage(current => (current === "mapping" ? "ready" : current))}
                onContinue={() => mappedTotal > 0 && setStage("confirm")}
            />
            <ImportOverwriteConfirmDialog
                open={stage === "confirm"}
                companyName={companyName}
                companyCount={mappedTotal}
                isSubmitting={isSubmitting}
                // Ignore a dismiss (Esc / overlay) while the import is in flight so the user can't
                // bounce back to mapping mid-request; the confirm button is disabled meanwhile too.
                onBack={() =>
                    !isSubmitting &&
                    setStage(current => (current === "confirm" ? "mapping" : current))
                }
                onConfirm={handleImport}
            />
        </SBox>
    );
};

export default DataTransferImportPanel;
